/**
 * RenderProgress - Render progress overlay for narrowcasting
 */
import React from 'react';
import {
    Box,
    Paper,
    Typography,
    LinearProgress,
    Button,
    Chip,
    Alert,
} from '@mui/material';
import {
    Download as DownloadIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    Movie as MovieIcon,
} from '@mui/icons-material';

export default function RenderProgress({ renderStatus, onDownload, onClose }) {
    if (!renderStatus) return null;

    const { totalProgress, allSlidesCompleted, anyFailed, slides, status, outputUrl, outputFilename } = renderStatus;

    const isCompleted = status === 'completed';
    const isConcatenating = status === 'concatenating';

    return (
        <Paper
            elevation={3}
            sx={{
                p: 3,
                mb: 3,
                border: '1px solid',
                borderColor: isCompleted ? '#10b981' : anyFailed ? '#ef4444' : '#6366f1',
                borderRadius: 2,
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <MovieIcon sx={{ color: isCompleted ? '#10b981' : '#6366f1' }} />
                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                    {isCompleted ? 'Video klaar!' : isConcatenating ? 'Video samenvoegen...' : 'Slides renderen...'}
                </Typography>
                <Box sx={{ flexGrow: 1 }} />
                <Chip
                    label={`${totalProgress}%`}
                    size="small"
                    sx={{
                        bgcolor: isCompleted ? '#d1fae5' : '#dbeafe',
                        color: isCompleted ? '#059669' : '#3b82f6',
                        fontWeight: 700,
                    }}
                />
            </Box>

            {/* Overall progress bar */}
            <LinearProgress
                variant="determinate"
                value={totalProgress}
                sx={{
                    mb: 2,
                    height: 8,
                    borderRadius: 4,
                    bgcolor: '#e5e7eb',
                    '& .MuiLinearProgress-bar': {
                        bgcolor: isCompleted ? '#10b981' : anyFailed ? '#ef4444' : '#6366f1',
                        borderRadius: 4,
                    },
                }}
            />

            {/* Per-slide status */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                {slides?.map((s) => (
                    <Chip
                        key={s.slideId}
                        label={`Slide ${s.sortOrder + 1}`}
                        size="small"
                        icon={
                            s.status === 'completed' ? <CheckCircleIcon /> :
                            s.status === 'error' || s.status === 'failed' ? <ErrorIcon /> :
                            null
                        }
                        sx={{
                            bgcolor: s.status === 'completed' ? '#d1fae5' :
                                     s.status === 'error' || s.status === 'failed' ? '#fee2e2' :
                                     s.status === 'rendering' ? '#fef3c7' : '#f3f4f6',
                            color: s.status === 'completed' ? '#059669' :
                                   s.status === 'error' || s.status === 'failed' ? '#dc2626' :
                                   s.status === 'rendering' ? '#d97706' : '#6b7280',
                            fontSize: '0.75rem',
                        }}
                    />
                ))}

                {isConcatenating && (
                    <Chip
                        label="Samenvoegen"
                        size="small"
                        sx={{ bgcolor: '#fef3c7', color: '#d97706', fontWeight: 600 }}
                    />
                )}
            </Box>

            {anyFailed && (
                <Alert severity="error" sx={{ mb: 2, fontSize: '0.85rem' }}>
                    Een of meer slides zijn mislukt. Probeer het opnieuw.
                </Alert>
            )}

            {/* Download button */}
            {isCompleted && (
                <Button
                    variant="contained"
                    startIcon={<DownloadIcon />}
                    onClick={onDownload}
                    sx={{
                        bgcolor: '#10b981',
                        '&:hover': { bgcolor: '#059669' },
                        textTransform: 'none',
                        fontWeight: 600,
                    }}
                >
                    Video downloaden {outputFilename && `(${outputFilename})`}
                </Button>
            )}
        </Paper>
    );
}
